import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useQuery } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { InputWithLabel } from '@/components/ui/input';
import { Button, ButtonLoading } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { UserProfile } from '@/graphql/types/admin';
import type { Plan } from '@/graphql/types/admin';
import { QUERY_PLANS_BY_ACTIVE } from '@/graphql/queries/plans';

export interface EditUserFormValues {
  email: string;
  display_name: string;
  phone_number: string;
  disabled: boolean;
  plan_id: string;
}

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: EditUserFormValues) => Promise<void>;
  loading: boolean;
  initialData?: UserProfile | null;
}

const validationSchema = yup.object().shape({
  email: yup.string().email('Email inválido').required('Email é obrigatório'),
  display_name: yup.string(),
  phone_number: yup.string(),
  disabled: yup.boolean(),
  plan_id: yup.string(),
});

export function EditUserDialog({
  open,
  onOpenChange,
  onSubmit,
  loading,
  initialData,
}: EditUserDialogProps) {
  const initialValues: EditUserFormValues = {
    email: initialData?.email ?? '',
    display_name: initialData?.display_name ?? '',
    phone_number: '',
    disabled: initialData?.disabled ?? false,
    plan_id: initialData?.plan?.id ?? '',
  };

  const { data: plansData } = useQuery<{ plans: Plan[] }>(QUERY_PLANS_BY_ACTIVE, {
    variables: { active: true },
    skip: !open,
  });
  const plans = plansData?.plans ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-6">
        <DialogHeader className="w-fit">
          <DialogTitle>Editar usuário</DialogTitle>
        </DialogHeader>

        <Formik
          key={initialData?.id ?? 'new'}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {({ values, errors, setFieldValue }) => (
            <Form className="flex flex-col gap-6 w-full">
              <InputWithLabel
                label="Email"
                id="email"
                name="email"
                type="email"
                value={values.email}
                onChange={(e) => setFieldValue('email', e.target.value)}
                error={Boolean(errors.email)}
                helperText={errors.email}
                className="w-full max-w-full"
              />

              <InputWithLabel
                label="Nome"
                id="display_name"
                name="display_name"
                value={values.display_name}
                onChange={(e) => setFieldValue('display_name', e.target.value)}
                error={Boolean(errors.display_name)}
                helperText={errors.display_name}
                className="w-full max-w-full"
              />

              <InputWithLabel
                label="Telefone"
                id="phone_number"
                name="phone_number"
                value={values.phone_number}
                onChange={(e) => setFieldValue('phone_number', e.target.value)}
                error={Boolean(errors.phone_number)}
                helperText={errors.phone_number}
                className="w-full max-w-full"
              />

              <div className="flex flex-col gap-2 w-full max-w-full">
                <Label htmlFor="plan_id">Plano</Label>
                <Select
                  name="plan_id"
                  value={values.plan_id || 'none'}
                  onValueChange={(value) => setFieldValue('plan_id', value === 'none' ? '' : value)}
                >
                  <SelectTrigger className="h-8 w-full">
                    <SelectValue placeholder="Selecione o plano" />
                  </SelectTrigger>
                  <SelectContent side="bottom">
                    <SelectItem value="none">Nenhum plano</SelectItem>
                    {plans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={!values.disabled}
                  onCheckedChange={(checked) => setFieldValue('disabled', !checked)}
                />
                <Label htmlFor="active">Ativo</Label>
              </div>

              <DialogFooter className="flex-col">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
                <ButtonLoading
                  variant="default"
                  label="Salvar alterações"
                  loading={loading}
                  type="submit"
                  disabled={loading || Boolean(errors.email)}
                />
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
